import React, { useState, useEffect, useCallback } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import { Button, Space, Modal, Select, Typography, Tooltip, Badge } from "antd";
import {
	PlusOutlined,
	PauseCircleOutlined,
	PlayCircleOutlined,
	EyeOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { fetchPatientPlans, fetchTemplates, assignPlan, pausePlan, resumePlan } from "../../../../api/healthcare/TreatmentPlanApi";
import {
	PatientTreatmentPlan,
	TreatmentPlanTemplate,
} from "../../../../types/healthcare/TreatmentPlanTypes";
import { User } from "../../../../types/admin/UserTypes";
import { useNotifications } from "../../../../context/NotificationContext";

import PlanDetailDrawer from "../../treatmentPlans/planComponents/PlanDetailDrawer";
import { DataTable, StatusTag } from "../../../../components/shared";


const { Text } = Typography;

interface TreatmentPlanTabProps {
	patient: Patient;
}

const TreatmentPlanTab: React.FC<TreatmentPlanTabProps> = ({ patient }) => {
	const user = getSessionUser() as User;
	const { showSuccess, showError } = useNotifications();

	const [plans, setPlans] = useState<PatientTreatmentPlan[]>([]);
	const [templates, setTemplates] = useState<TreatmentPlanTemplate[]>([]);
	const [loading, setLoading] = useState(false);

	// Assign modal
	const [assignOpen, setAssignOpen] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState<number | undefined>();
	const [assigning, setAssigning] = useState(false);

	// Detail drawer
	const [detailPlan, setDetailPlan] = useState<PatientTreatmentPlan | null>(null);

	const load = useCallback(async () => {
		if (!patient.id) return;
		setLoading(true);
		try {
			const [planList, tpls] = await Promise.all([fetchPatientPlans(patient.id), fetchTemplates()]);
			setPlans(planList);
			setTemplates(tpls);
		} catch {
			showError("Failed to load treatment plans");
		} finally {
			setLoading(false);
		}
	}, [patient.id]);

	useEffect(() => {
		load();
	}, [load]);

	const handleAssign = async () => {
		if (!selectedTemplate) return;
		setAssigning(true);
		try {
			await assignPlan({
				patient_id: patient.id,
				practitioner_id: user.id,
				treatment_plan_template_id: selectedTemplate,
			});
			showSuccess("Treatment plan assigned");
			setAssignOpen(false);
			setSelectedTemplate(undefined);
			load();
		} catch (err: any) {
			showError(err?.response?.data?.message ?? "Failed to assign plan");
		} finally {
			setAssigning(false);
		}
	};

	const handlePause = async (plan: PatientTreatmentPlan) => {
		try {
			await pausePlan(plan.id);
			showSuccess("Plan paused");
			load();
			if (detailPlan?.id === plan.id) setDetailPlan((prev) => prev ? { ...prev, status: "paused" } : null);
		} catch {
			showError("Failed to pause plan");
		}
	};

	const handleResume = async (plan: PatientTreatmentPlan) => {
		try {
			await resumePlan(plan.id);
			showSuccess("Plan resumed");
			load();
			if (detailPlan?.id === plan.id) setDetailPlan((prev) => prev ? { ...prev, status: "active" } : null);
		} catch {
			showError("Failed to resume plan");
		}
	};

	const columns: ColumnsType<PatientTreatmentPlan> = [
		{
			title: "Plan",
			render: (_, r) => (
				<Text>{r.template?.name ?? `Plan #${r.id}`}</Text>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			render: (s) => <StatusTag status={s} label={s.charAt(0).toUpperCase() + s.slice(1)} />,
		},
		{
			title: "Started",
			dataIndex: "started_at",
			render: (v) => (v ? <Text type="secondary">{dayjs(v).format("D MMM YY")}</Text> : "—"),
		},
		{
			title: "",
			fixed: "right",
			render: (_, record) => (
				<Space size={4}>
					<Tooltip title="View plan details">
						<Button
							size="small"
							type="default"
							ghost
							icon={<EyeOutlined />}
							onClick={() => setDetailPlan({ ...record, patient })}
						>
							View
						</Button>
					</Tooltip>
					{record.status === "active" && (
						<Tooltip title="Pause plan">
							<Button size="small" icon={<PauseCircleOutlined />} onClick={() => handlePause(record)} />
						</Tooltip>
					)}
					{record.status === "paused" && (
						<Tooltip title="Resume plan">
							<Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => handleResume(record)} />
						</Tooltip>
					)}
				</Space>
			),
		},
	];

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			{/* Header */}
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setAssignOpen(true)}>
					Assign Treatment Plan
				</Button>
			</div>

			{/* Plans table */}
			<DataTable
				columns={columns}
				dataSource={plans}
				rowKey="id"
				loading={loading}
				pagination={false}
				emptyTitle="No treatment plans assigned"
				fillHeight={false}
			/>

			{/* Assign Modal */}
			<Modal
				title="Assign Treatment Plan"
				open={assignOpen}
				onCancel={() => {
					setAssignOpen(false);
					setSelectedTemplate(undefined);
				}}
				onOk={handleAssign}
				okText="Assign Plan"
				okButtonProps={{ disabled: !selectedTemplate, loading: assigning }}
			>
				<div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
					<div>
						<Text strong>Select Template</Text>
						<Select
							value={selectedTemplate}
							onChange={setSelectedTemplate}
							placeholder="Search treatment plan templates…"
							style={{ width: "100%", marginTop: 6 }}
							showSearch
							filterOption={(input, option) =>
								String(option?.label ?? "")
									.toLowerCase()
									.includes(input.toLowerCase())
							}
							options={templates
								.filter((t) => t.is_active)
								.map((t) => ({
									value: t.id,
									label: t.name,
									title: t.description,
								}))}
						/>
					</div>
				</div>
			</Modal>

			{/* Plan Detail Drawer */}
			{detailPlan && (
				<PlanDetailDrawer
					plan={detailPlan}
					onClose={() => setDetailPlan(null)}
					onPlanChange={load}
				/>
			)}
		</div>
	);
};

export default TreatmentPlanTab;
